import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, getToolName, isToolUIPart, type UIMessage } from "ai";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type AgentChatMessage, type AgentChatSession } from "@fitness-app/data-models";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { mapStoredMessage } from "~/modules/Sales/SalesAgent/messageUtils";
import { analyticsService, supabase } from "~/store/initializeStore";

import { type TrainerAssistantMention } from "./mentions";

// TRE-2313 — cała logika rozmowy z asystentem. Żyje w hoście (trwały layout), żeby
// pływające okno i tryb pełnoekranowy pokazywały tę samą rozmowę, a przełączenie widoku
// nie przerywało odpowiedzi. Sesję zakłada endpoint (nagłówek X-Agent-Chat-Session-Id),
// on też zapisuje odpowiedź; lista rozmów i historia idą z agent_chat_* pod RLS.

const agentType = "trainer_assistant";
const sessionsKey = ["trainerAssistantSessions"];
const messagesKey = (sessionId: string | null) => ["trainerAssistantMessages", sessionId];

// Wspólny mapper bierze id wiersza i gubi metadata. Tu potrzebne są oba: `uiMessageId`
// (to id zna serwer - „Ponów” ucina historię po tej wiadomości) i metadata (wzmianki, ocena).
const toUiMessage = (row: AgentChatMessage): UIMessage => {
  const metadata = (row.metadata ?? {}) as { uiMessageId?: unknown };
  return {
    ...mapStoredMessage(row),
    id: typeof metadata.uiMessageId === "string" ? metadata.uiMessageId : row.id,
    metadata: row.metadata ?? undefined,
  };
};

export type MessageRating = "up" | "down";

export const readMessageRating = (metadata: unknown): MessageRating | null => {
  const rating = (metadata as { rating?: unknown } | null | undefined)?.rating;
  return rating === "up" || rating === "down" ? rating : null;
};

const toolNamesOf = (assistantMessage: UIMessage) => [
  ...new Set(assistantMessage.parts.filter(isToolUIPart).map((part) => getToolName(part))),
];

export const useTrainerAssistantChat = ({ visible }: { visible: boolean }) => {
  const queryClient = useQueryClient();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [unread, setUnread] = useState(false);
  // Szkic i wybrane wzmianki żyją tu, a nie w kompozerze: okno i tryb pełnoekranowy to
  // dwa osobne kompozery, a przełączenie widoku nie może gubić wpisanego tekstu.
  const [draft, setDraft] = useState("");
  const pickedMentions = useRef(new Map<string, TrainerAssistantMention>());
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const activeSessionIdRef = useRef<string | null>(null);
  // Blokuje hydrację z bazy na czas odpowiedzi dla danej sesji: nieświeży (albo pusty,
  // dla nowej sesji) wynik zapytania nadpisałby rozmowę w chwili, gdy `busy` spada.
  const skipHydrationSessionIdRef = useRef<string | null>(null);
  const skipHydrationTokenRef = useRef(0);

  const { data: sessions } = useQuery({
    queryKey: sessionsKey,
    enabled: visible,
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      // RLS pozwala head coachowi czytać rozmowy zespołu - historia pokazuje tylko własne.
      const { data, error } = await supabase
        .from("agent_chat_session")
        .select("*")
        .eq("agentType", agentType)
        .eq("ownerId", user?.id ?? "")
        .order("lastMessageAt", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatSession[];
    },
  });

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${getClientWebApiBaseUrl()}/api/trainers/ai/trainer-assistant-chat`,
        fetch: async (request, init) => {
          const response = await fetch(request, init);
          const sessionId = response.headers.get("X-Agent-Chat-Session-Id");
          if (sessionId) {
            activeSessionIdRef.current = sessionId;
            skipHydrationSessionIdRef.current = sessionId;
            setActiveSessionId(sessionId);
            void queryClient.invalidateQueries({ queryKey: sessionsKey });
          }
          return response;
        },
        prepareSendMessagesRequest: async ({ api, body, headers, credentials, messages }) => {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          const latestMessage = messages.at(-1);
          return {
            api,
            credentials,
            headers: {
              ...headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: {
              ...body,
              sessionId: activeSessionIdRef.current ?? undefined,
              messages: latestMessage ? [latestMessage] : [],
            },
          };
        },
      }),
    [queryClient],
  );

  const chat = useChat<UIMessage>({
    transport,
    experimental_throttle: 50,
    onFinish: async ({ message: assistantMessage, isAbort, isError }) => {
      if (!isAbort && !isError) {
        analyticsService.track("trainer_assistant_replied", {
          sessionId: activeSessionIdRef.current,
          tools: toolNamesOf(assistantMessage),
          toolCount: assistantMessage.parts.filter(isToolUIPart).length,
        });
        if (!visibleRef.current) setUnread(true);
      }
      const skipHydrationToken = skipHydrationTokenRef.current;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: sessionsKey }),
        activeSessionIdRef.current
          ? queryClient.invalidateQueries({ queryKey: messagesKey(activeSessionIdRef.current) })
          : Promise.resolve(),
      ]);
      if (skipHydrationTokenRef.current === skipHydrationToken) {
        skipHydrationSessionIdRef.current = null;
      }
    },
  });
  const { sendMessage, setMessages, status, clearError } = chat;
  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    if (visible) setUnread(false);
  }, [visible]);

  const { data: storedMessages } = useQuery({
    queryKey: messagesKey(activeSessionId),
    enabled: Boolean(activeSessionId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", activeSessionId!)
        .order("createdAt", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });

  useEffect(() => {
    if (!storedMessages || busy) return;
    if (activeSessionId && skipHydrationSessionIdRef.current === activeSessionId) return;
    setMessages(storedMessages.map(toUiMessage));
  }, [activeSessionId, busy, setMessages, storedMessages]);

  const selectSession = useCallback(
    (sessionId: string) => {
      activeSessionIdRef.current = sessionId;
      skipHydrationTokenRef.current += 1;
      skipHydrationSessionIdRef.current = null;
      setActiveSessionId(sessionId);
      setMessages([]);
      clearError();
    },
    [clearError, setMessages],
  );

  const startNew = useCallback(() => {
    activeSessionIdRef.current = null;
    skipHydrationTokenRef.current += 1;
    skipHydrationSessionIdRef.current = null;
    setActiveSessionId(null);
    setMessages([]);
    clearError();
  }, [clearError, setMessages]);

  const send = useCallback(
    (text: string, mentions: TrainerAssistantMention[]) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return false;
      clearError();
      skipHydrationTokenRef.current += 1;
      skipHydrationSessionIdRef.current = activeSessionIdRef.current;
      void sendMessage({
        id: crypto.randomUUID(),
        role: "user",
        parts: [{ type: "text", text: trimmed }],
        ...(mentions.length ? { metadata: { mentions } } : {}),
      });
      return true;
    },
    [busy, clearError, sendMessage],
  );

  // Ocena odpowiedzi: PostHog (adopcja, jakość) i zapis w wiadomości, żeby było ją widać
  // przy kolejnym otwarciu rozmowy. RLS pozwala właścicielowi edytować własne wiadomości.
  const rateMessage = useCallback(
    async (target: UIMessage, rating: MessageRating | null) => {
      const sessionId = activeSessionIdRef.current;
      setMessages((current) =>
        current.map((item) =>
          item.id === target.id ? { ...item, metadata: { ...(item.metadata as object), rating } } : item,
        ),
      );
      analyticsService.track("trainer_assistant_rated", { sessionId, rating, tools: toolNamesOf(target) });
      if (!sessionId) return;

      const { data: rows, error } = await supabase
        .from("agent_chat_message")
        .select("id, metadata")
        .eq("sessionId", sessionId)
        .or(`id.eq.${target.id},metadata->>uiMessageId.eq.${target.id}`)
        .limit(1);
      const row = rows?.[0];
      if (error || !row) return;
      await supabase
        .from("agent_chat_message")
        .update({ metadata: { ...((row.metadata ?? {}) as object), rating } })
        .eq("id", row.id);
    },
    [setMessages],
  );

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error: deleteError } = await supabase.from("agent_chat_session").delete().eq("id", sessionId);
      if (deleteError) throw new Error(deleteError.message);
    },
    onSuccess: async (_, sessionId) => {
      if (sessionId === activeSessionIdRef.current) startNew();
      analyticsService.track("trainer_assistant_session_deleted", { sessionId });
      await queryClient.invalidateQueries({ queryKey: sessionsKey });
      void message.success("Rozmowa usunięta");
    },
    onError: () => void message.error("Nie udało się usunąć rozmowy"),
  });

  return {
    ...chat,
    activeSessionId,
    busy,
    draft,
    pickedMentions,
    setDraft,
    deleteSession: deleteSessionMutation.mutate,
    rateMessage,
    selectSession,
    send,
    sessions: sessions ?? [],
    startNew,
    unread,
  };
};

export type TrainerAssistantChat = ReturnType<typeof useTrainerAssistantChat>;
